import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, ShoppingBag, Smartphone, Car, Home, Shirt, Dumbbell, BookOpen, Wrench, Laptop, MoreHorizontal, ArrowRight, Shield, Zap, CreditCard, Star, ChevronRight, MapPin, Camera, MessageCircle, Package, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';

const CATEGORIES = [
  { name: 'Eletrônicos', icon: Smartphone, color: 'bg-blue-50 text-blue-600' },
  { name: 'Veículos', icon: Car, color: 'bg-orange-50 text-orange-600' },
  { name: 'Imóveis', icon: Home, color: 'bg-purple-50 text-purple-600' },
  { name: 'Moda', icon: Shirt, color: 'bg-pink-50 text-pink-600' },
  { name: 'Esportes', icon: Dumbbell, color: 'bg-green-50 text-green-600' },
  { name: 'Livros', icon: BookOpen, color: 'bg-amber-50 text-amber-600' },
  { name: 'Serviços', icon: Wrench, color: 'bg-cyan-50 text-cyan-600' },
  { name: 'Digital', icon: Laptop, color: 'bg-violet-50 text-violet-600' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);

  useEffect(() => {
    api.get('/products/featured').then(r => setFeaturedProducts(r.data.products)).catch(() => {});
    api.get('/products?limit=8&sort=recent').then(r => setRecentProducts(r.data.products)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="min-h-screen">
      {/* Featured Products - Top */}
      {(featuredProducts.length > 0 || recentProducts.length > 0) && (
        <section className="bg-gradient-to-b from-emerald-50/50 to-white py-8" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg sm:text-xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>Destaques</h2>
              <Link to="/products?sort=popular" className="text-sm text-primary hover:underline flex items-center gap-1">
                Ver todos <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {(featuredProducts.length > 0 ? featuredProducts : recentProducts).slice(0, 6).map((p) => (
                <ProductCard key={p._id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hero - Text + Search */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-foreground leading-[1.1] mb-4" style={{ fontFamily: 'Outfit' }}>
              Compre e venda<br />
              <span className="text-primary">de tudo</span> aqui
            </h1>
            <p className="text-base text-muted-foreground mb-6 max-w-xl mx-auto">
              Produtos físicos, digitais e serviços. Negocie diretamente com outros brasileiros de forma segura.
            </p>
            <form onSubmit={handleSearch} className="flex max-w-xl mx-auto border-2 border-primary rounded-full px-2 py-1.5 bg-white shadow-lg shadow-primary/5" data-testid="hero-search-form">
              <Search className="h-5 w-5 text-muted-foreground ml-3 mt-2 shrink-0" />
              <input
                type="text"
                placeholder="O que você está procurando?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none text-sm sm:text-base px-3 py-2"
                data-testid="hero-search-input"
              />
              <Button type="submit" className="rounded-full px-6" data-testid="hero-search-btn">Buscar</Button>
            </form>
          </div>
        </div>
      </section>

      {/* Promotional Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="promo-banner-section">
        <Link to="/products">
          <img
            src="/banner.png"
            alt="Venda de Garagem - Achou, Levou, Sorriu! De tudo um pouco, para todo mundo!"
            className="w-full rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
          />
        </Link>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="categories-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>Categorias</h2>
          <Link to="/products" className="text-sm text-primary hover:underline flex items-center gap-1">
            Ver todas <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.name}
              to={`/products?category=${encodeURIComponent(cat.name)}`}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-200 group"
              data-testid={`category-${cat.name}`}
            >
              <div className={`p-3 rounded-xl ${cat.color} group-hover:scale-110 transition-transform duration-200`}>
                <cat.icon className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium text-center">{cat.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Recent Products */}
      {recentProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="recent-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>Recém Publicados</h2>
            <Link to="/products?sort=recent" className="text-sm text-primary hover:underline flex items-center gap-1">
              Ver todos <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {recentProducts.slice(0, 8).map((p) => <ProductCard key={p._id} product={p} />)}
          </div>
        </section>
      )}

      {/* How it works - Guide */}
      <section className="bg-muted/50 py-16" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold text-center mb-3" style={{ fontFamily: 'Outfit' }}>
            Como funciona a Venda de Garagem
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-10 max-w-xl mx-auto">
            Guia completo para vender e comprar com segurança na plataforma
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Guide: How to Sell */}
            <div className="p-6 rounded-2xl border border-border bg-card" data-testid="guide-sell">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Como Anunciar</h3>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</span>
                  <div>
                    <p className="text-sm font-medium">Crie sua conta gratuitamente</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Cadastre-se com email e confirme com o código de 6 dígitos.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">2</span>
                  <div>
                    <p className="text-sm font-medium">Clique em &ldquo;Anunciar&rdquo; e preencha os dados</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Título, descrição, preço, categoria, condição e até 8 fotos.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">3</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium">Selecione o Estado e Cidade corretamente</p>
                      <MapPin className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="mt-1 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="text-xs text-amber-800 leading-relaxed">
                        <strong>Importante:</strong> A localização do anúncio é essencial! Compradores usam o filtro de estado e cidade para encontrar produtos perto deles. Quanto mais precisa a localização, mais chances de venda.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">4</span>
                  <div>
                    <p className="text-sm font-medium">Aguarde contato dos compradores via Chat</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Negocie diretamente pelo chat da plataforma. Pagamento seguro com sistema Escrow.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Guide: How to Buy */}
            <div className="p-6 rounded-2xl border border-border bg-card" data-testid="guide-buy">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-blue-50">
                  <ShoppingBag className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Como Comprar</h3>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                  <div>
                    <p className="text-sm font-medium">Busque o produto desejado</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Use a barra de busca ou navegue pelas categorias.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium">Filtre por Estado e Cidade</p>
                      <MapPin className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div className="mt-1 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                      <p className="text-xs text-blue-800 leading-relaxed">
                        <strong>Dica importante:</strong> Use os filtros de localização na página de produtos para encontrar itens perto de você. Isso facilita a entrega, reduz custos de frete e permite encontros presenciais seguros.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                  <div>
                    <p className="text-sm font-medium">Converse com o vendedor pelo Chat</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Tire dúvidas, combine a entrega e negocie antes de comprar.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">4</span>
                  <div>
                    <p className="text-sm font-medium">Compre com segurança e avalie</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Pagamento via PIX ou Cripto com Escrow. Após receber, avalie o vendedor com 1 a 5 estrelas.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location tips */}
          <div className="mt-8 p-5 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-transparent to-blue-50" data-testid="location-tips-banner">
            <div className="flex gap-4 items-start">
              <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-1.5" style={{ fontFamily: 'Outfit' }}>Sobre a localização dos anúncios</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Todos os anúncios são visíveis para todos os usuários do Brasil. Porém, recomendamos sempre verificar o <strong>estado e cidade</strong> do vendedor antes de comprar. 
                  Use os filtros na página de produtos para encontrar ofertas na sua região. Para produtos físicos, priorize vendedores próximos para facilitar a entrega ou retirada.
                </p>
                <div className="flex flex-wrap gap-3 mt-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-full px-3 py-1.5">
                    <AlertTriangle className="h-3 w-3 text-amber-500" /> Sempre confira a cidade do vendedor
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-full px-3 py-1.5">
                    <MessageCircle className="h-3 w-3 text-blue-500" /> Combine a entrega pelo Chat
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-full px-3 py-1.5">
                    <Camera className="h-3 w-3 text-green-500" /> Peça fotos extras se precisar
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Payments */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="features-section">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-8 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow">
            <Shield className="h-10 w-10 text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Segurança com Wise Eagle</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Em breve: verifique anúncios automaticamente com inteligência artificial para evitar golpes e fraudes.
            </p>
          </div>
          <div className="p-8 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow">
            <CreditCard className="h-10 w-10 text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Pagamentos via OASIS</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Pague com PIX ou criptomoedas pela plataforma OASIS. Transações seguras e instantâneas.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      {!user && (
        <section className="bg-primary py-16" data-testid="cta-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-primary-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
              Comece a vender agora
            </h2>
            <p className="text-primary-foreground/80 mb-8 max-w-md mx-auto text-sm">
              Cadastre-se gratuitamente e publique seu primeiro anúncio.
            </p>
            <Link to="/register" data-testid="cta-register-btn">
              <Button size="lg" variant="secondary" className="rounded-full px-8 font-semibold">
                Criar conta grátis <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
